<script lang="ts">
	import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
	import type { TextItem } from 'pdfjs-dist/types/src/display/api';
	import type { SourceBlock } from 'src/util/citation-transformer';

	GlobalWorkerOptions.workerSrc = new URL(
		'pdfjs-dist/build/pdf.worker.min.mjs',
		import.meta.url
	).href;

	export interface Block {
		text: string;
		page: number;
		bbox: [number, number, number, number];
		block_class?: string | null;
		tokens?: number;
		summary_relevant?: boolean;
	}

	export interface MarginConfig {
		/** Header crop zone height in PDF points (px at 1x scale). Applied to all pages except the first. */
		header?: number;
		/** Footer crop zone height in PDF points (px at 1x scale). */
		footer?: number;
		/** Header crop zone height for the first page only (title pages often have a taller header). */
		firstPageHeader?: number;
	}

	interface Props {
		/** URL of the PDF to render. External URLs are proxied via /api/proxy-pdf. */
		url?: string;
		/** Extracted text blocks with bounding boxes, used for the block overlay. */
		blocks?: Block[] | null;
		/** Show bounding box overlay for all blocks (debug/inspection tool). */
		showBlocks?: boolean;
		/** Highlight the header/footer margin zones with a yellow dashed overlay. */
		highlightMargins?: boolean;
		/** Margin crop configuration (header, footer, firstPageHeader in PDF points). */
		margins?: MarginConfig;
		/** Highlight the first occurrence of this string in the rendered PDF. */
		highlightText?: string | null;
		/** Citation source blocks to highlight by bounding box. */
		highlightBlocks?: SourceBlock[] | null;
	}

	let {
		url,
		blocks = null,
		showBlocks = false,
		highlightMargins = false,
		margins = {},
		highlightText = null,
		highlightBlocks = null
	}: Props = $props();

	const headerMargin = $derived(margins.header ?? 0);
	const footerMargin = $derived(margins.footer ?? 0);
	const firstPageHeaderMargin = $derived(margins.firstPageHeader ?? 0);

	const SCALE = 2;

	interface RenderedPage {
		dataUrl: string;
		width: number;
		height: number;
		pageIndex: number;
		textItems: Array<{ str: string; transform: number[]; width: number; height: number }>;
	}

	let pages = $state<RenderedPage[]>([]);
	let loading = $state(false);
	let loadError = $state<string | null>(null);
	let zoom = $state(1);
	let controlsVisible = $state(false);
	let hideTimeout: ReturnType<typeof setTimeout> | null = null;
	let scrollArea: HTMLElement;

	const ZOOM_STEP = 0.25;
	const ZOOM_MIN = 1;
	const ZOOM_MAX = 4;

	function showControls() {
		controlsVisible = true;
		if (hideTimeout) {
			clearTimeout(hideTimeout);
		}
		hideTimeout = setTimeout(() => (controlsVisible = false), 2000);
	}

	$effect(() => {
		if (!scrollArea) {
			return;
		}

		let pinchStartDist: number | null = null;
		let pinchStartZoom = 1;

		function getTouchDist(touches: TouchList): number {
			const dx = touches[0].clientX - touches[1].clientX;
			const dy = touches[0].clientY - touches[1].clientY;
			return Math.hypot(dx, dy);
		}

		function onTouchStart(e: TouchEvent) {
			if (e.touches.length === 2) {
				pinchStartDist = getTouchDist(e.touches);
				pinchStartZoom = zoom;
				showControls();
			}
		}

		function onTouchMove(e: TouchEvent) {
			if (e.touches.length === 2 && pinchStartDist !== null) {
				e.preventDefault();
				const dist = getTouchDist(e.touches);
				zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pinchStartZoom * (dist / pinchStartDist)));
				showControls();
			}
		}

		function onTouchEnd() {
			pinchStartDist = null;
		}

		scrollArea.addEventListener('touchstart', onTouchStart, { passive: true });
		scrollArea.addEventListener('touchmove', onTouchMove, { passive: false });
		scrollArea.addEventListener('touchend', onTouchEnd);

		return () => {
			scrollArea.removeEventListener('touchstart', onTouchStart);
			scrollArea.removeEventListener('touchmove', onTouchMove);
			scrollArea.removeEventListener('touchend', onTouchEnd);
		};
	});

	$effect(() => {
		const rawUrl = url ?? '';
		if (!rawUrl) {
			return;
		}

		const effectiveUrl =
			rawUrl.startsWith('http://') || rawUrl.startsWith('https://')
				? `/api/proxy-pdf?url=${encodeURIComponent(rawUrl)}`
				: rawUrl;

		let cancelled = false;
		loading = true;
		loadError = null;
		pages = [];

		(async () => {
			try {
				const rendered = await renderPdf(effectiveUrl);
				if (!cancelled) {
					pages = rendered;
					loading = false;
				}
			} catch (e) {
				if (!cancelled) {
					loadError = String(e);
					loading = false;
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	});

	async function renderPdf(docUrl: string): Promise<RenderedPage[]> {
		const pdf = await getDocument(docUrl).promise;
		const result: RenderedPage[] = [];

		for (let i = 1; i <= pdf.numPages; i++) {
			const page = await pdf.getPage(i);
			const viewport = page.getViewport({ scale: SCALE });

			const canvas = document.createElement('canvas');
			canvas.width = Math.floor(viewport.width);
			canvas.height = Math.floor(viewport.height);
			await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;

			const textContent = await page.getTextContent();
			const textItems = textContent.items
				.filter((item): item is TextItem => 'str' in item)
				.map((item) => ({
					str: item.str,
					transform: item.transform as number[],
					width: item.width,
					height: item.height
				}));

			result.push({
				dataUrl: canvas.toDataURL(),
				width: canvas.width,
				height: canvas.height,
				pageIndex: i - 1,
				textItems
			});
		}

		return result;
	}

	function normalizeText(text: string): string {
		return text
			.replace(/[\u2010-\u2015\u2212]/g, '-')
			.replace(/[\u00A0\u202F\u2009]/g, ' ')
			.replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"')
			.replace(/[\u2018\u2019\u201A\u201B]/g, "'")
			.replace(/\s+/g, ' ')
			.trim()
			.toLowerCase();
	}

	interface TextHighlight {
		pageIndex: number;
		x: number;
		y: number;
		width: number;
		height: number;
	}

	const textHighlights = $derived.by(() => {
		if (!highlightText || pages.length === 0) {
			return [] as TextHighlight[];
		}
		const normalizedSearch = normalizeText(highlightText);
		if (normalizedSearch.length < 10) {
			return [] as TextHighlight[];
		}

		const results: TextHighlight[] = [];

		for (const page of pages) {
			const { textItems, height: pageHeight } = page;

			let fullText = '';
			const itemPositions: Array<{ start: number; end: number; idx: number }> = [];

			for (let i = 0; i < textItems.length; i++) {
				const start = fullText.length;
				fullText += textItems[i].str;
				itemPositions.push({ start, end: fullText.length, idx: i });
				if (
					i < textItems.length - 1 &&
					!textItems[i].str.endsWith(' ') &&
					!textItems[i + 1].str.startsWith(' ')
				) {
					fullText += ' ';
				}
			}

			const normalizedFull = normalizeText(fullText);
			const matchIdx = normalizedFull.indexOf(normalizedSearch);
			if (matchIdx === -1) {
				continue;
			}

			const matchEnd = matchIdx + normalizedSearch.length;
			const matchingItems = itemPositions.filter((pos) => {
				const normalizedStart = normalizeText(fullText.substring(0, pos.start)).length;
				const normalizedEnd = normalizeText(fullText.substring(0, pos.end)).length;
				return normalizedEnd > matchIdx && normalizedStart < matchEnd;
			});

			for (const pos of matchingItems) {
				const item = textItems[pos.idx];
				const [, , , , tx, ty] = item.transform;
				const h = (item.height || 12) * SCALE;
				results.push({
					pageIndex: page.pageIndex,
					x: tx * SCALE,
					y: pageHeight - ty * SCALE - h,
					width: item.width * SCALE,
					height: h
				});
			}

			break; // only first match
		}

		return results;
	});
</script>

<div class="pdf-viewer" role="region" aria-label="PDF viewer" onmousemove={showControls}>
	<div class="zoom-controls" class:visible={controlsVisible}>
		<button
			class="icon-button"
			onclick={() => (zoom = Math.max(ZOOM_MIN, zoom - ZOOM_STEP))}
			disabled={zoom <= ZOOM_MIN}>−</button
		>
		<span>{Math.round(zoom * 100)}%</span>
		<button
			class="icon-button"
			onclick={() => (zoom = Math.min(ZOOM_MAX, zoom + ZOOM_STEP))}
			disabled={zoom >= ZOOM_MAX}>+</button
		>
	</div>
	<div class="scroll-area" bind:this={scrollArea}>
		{#if loading}
			<div class="status">Loading PDF…</div>
		{:else if loadError}
			<div class="status error">{loadError}</div>
		{:else}
			<div class="pages" style="width: {zoom * 100}%;">
				{#each pages as p (p.pageIndex)}
					{@const topPx = (p.pageIndex === 0 ? firstPageHeaderMargin : headerMargin) * SCALE}
					{@const botPx = footerMargin * SCALE}
					{@const pageTextHighlights = textHighlights.filter((h) => h.pageIndex === p.pageIndex)}
					{@const pageHighlightBlocks = (highlightBlocks ?? []).filter(
						(b) => b.page - 1 === p.pageIndex
					)}
					{@const hasOverlay =
						showBlocks ||
						highlightMargins ||
						pageTextHighlights.length > 0 ||
						pageHighlightBlocks.length > 0}
					<div class="page-wrap">
						<img src={p.dataUrl} width={p.width} height={p.height} alt="Page {p.pageIndex + 1}" />

						{#if hasOverlay}
							<svg
								class="overlay"
								viewBox="0 0 {p.width} {p.height}"
								xmlns="http://www.w3.org/2000/svg"
								preserveAspectRatio="none"
							>
								{#if highlightMargins}
									{#if topPx > 0}
										<rect
											x="0"
											y="0"
											width={p.width}
											height={topPx}
											style="fill: var(--overlay-margin-fill); stroke: var(--overlay-margin-stroke);"
											stroke-width="3"
											stroke-dasharray="10 6"
										/>
									{/if}
									{#if botPx > 0}
										<rect
											x="0"
											y={p.height - botPx}
											width={p.width}
											height={botPx}
											style="fill: var(--overlay-margin-fill); stroke: var(--overlay-margin-stroke);"
											stroke-width="3"
											stroke-dasharray="10 6"
										/>
									{/if}
								{/if}

								{#if showBlocks && blocks}
									{#each blocks.filter((b) => b.page - 1 === p.pageIndex) as block}
										<rect
											x={block.bbox[0] * SCALE}
											y={block.bbox[1] * SCALE}
											width={(block.bbox[2] - block.bbox[0]) * SCALE}
											height={(block.bbox[3] - block.bbox[1]) * SCALE}
											fill="rgba(0,140,255,0.08)"
											stroke="rgba(0,140,255,0.7)"
											stroke-width="2"
										/>
									{/each}
								{/if}

								{#each pageHighlightBlocks as block}
									<rect
										x={block.bbox[0] * SCALE}
										y={p.height - block.bbox[3] * SCALE}
										width={(block.bbox[2] - block.bbox[0]) * SCALE}
										height={(block.bbox[3] - block.bbox[1]) * SCALE}
										style="fill: var(--overlay-highlight-fill);"
										rx="2"
									/>
								{/each}

								{#each pageTextHighlights as h}
									<rect
										x={h.x}
										y={h.y}
										width={h.width}
										height={h.height}
										style="fill: var(--overlay-highlight-fill);"
										rx="2"
									/>
								{/each}
							</svg>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.pdf-viewer {
		@include lib.flex-column();
		overflow: hidden;
		position: relative;
		flex: 1 1 auto;

		--overlay-margin-fill: hsla(var(--accent-h), var(--accent-s), 60%, 0.18);
		--overlay-margin-stroke: hsla(var(--accent-h), var(--accent-s), 55%, 0.9);
		--overlay-highlight-fill: hsla(var(--accent-h), var(--accent-s), 65%, 0.35);
		max-height: 100%;

		.scroll-area {
			@include lib.flex-column();
			overflow: auto;
			flex: 1 1 auto;
			touch-action: pan-y;
		}

		.zoom-controls {
			position: absolute;
			top: 1rem;
			left: 50%;
			transform: translateX(-50%);
			z-index: 10;
			display: flex;
			align-items: center;
			gap: 0.5rem;
			padding: 0.5rem 0.75rem;
			border-radius: 8px;
			background: rgba(0, 0, 0, 0.65);
			backdrop-filter: blur(6px);
			font-size: 0.875rem;
			width: fit-content;
			margin: 0 auto;
			pointer-events: none;
			opacity: 0;
			transition: opacity 0.2s ease;

			&.visible {
				opacity: 1;
				pointer-events: auto;
			}

			button {
				padding: 0.25rem 0.5rem;
				border: 1px solid rgba(255, 255, 255, 0.25);
				border-radius: 4px;
				background: rgba(255, 255, 255, 0.1);
				color: #fff;
				cursor: pointer;
				font-size: 0.875rem;
				line-height: 1;

				&:hover:not(:disabled) {
					background: rgba(255, 255, 255, 0.2);
				}

				&:disabled {
					opacity: 0.35;
					cursor: default;
				}
			}

			span {
				min-width: 3rem;
				text-align: center;
				color: rgba(255, 255, 255, 0.85);
			}
		}

		.status {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			flex: 1;
			min-height: 100%;
			font-size: 0.875rem;
			color: var(--text-secondary);

			&.error {
				color: var(--color-error);
			}
		}

		.pages {
			display: flex;
			flex-direction: column;
			gap: 1rem;
			width: 100%;
			box-sizing: border-box;
		}

		.page-wrap {
			position: relative;
			width: 100%;
			border-radius: 0.5rem;
			overflow: hidden;

			img {
				display: block;
				width: 100%;
				height: auto;
			}

			.overlay {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				pointer-events: none;
			}
		}
	}
</style>
